import * as React from "react";

import { RotateCcwIcon } from "lucide-react";
import {
  useClearRefinements,
  useCurrentRefinements,
  useRefinementList,
} from "react-instantsearch";

import { PrimAccordion } from "@/shared-web.ui/prim.accordion";
import { semanticColor } from "@/shared-web.ui/styles";
import { Badge } from "@/shared-web.ui/ui.badge";
import { Button } from "@/shared-web.ui/ui.button";
import { Checkbox } from "@/shared-web.ui/ui.checkbox";
import { Flex } from "@/shared-web.ui/ui.flex";
import { ScrollArea } from "@/shared-web.ui/ui.scroll-area";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";

import { useProductListContext } from "../feat.product-list-context";
import { HierarchicalMenu } from "./hierarchical-menu";
import { RangeSlider } from "./range-slider";
import { ToggleSwitch } from "./toggle-switch";

type CurrentRefinementsConnectorParamsItem = ReturnType<typeof useCurrentRefinements>["items"][0];

const ProductHierarchical = () => {
  return (
    <HierarchicalMenu
      attributes={[
        "categoryHierarchySlug.lvl0",
        "categoryHierarchySlug.lvl1",
        "categoryHierarchySlug.lvl2",
      ]}
    />
  );
};

interface FacetRefinementListProps {
  attribute: string;
  attributeType: "attribute" | "variant";
}
const FacetRefinementList = (props: FacetRefinementListProps) => {
  const { attribute, attributeType } = props;

  const {
    items,
    refine,
  } = useRefinementList({
    attribute,
  });

  const { items: refinedItems } = useCurrentRefinements({
    includedAttributes: [attribute],
  });
  const [refinedItem, setRefinedItem] = React.useState<
    CurrentRefinementsConnectorParamsItem | null
  >(null);

  React.useEffect(() => {
    if (refinedItems.length > 0) {
      setRefinedItem(refinedItems[0]);
    }
    else {
      setRefinedItem(null);
    }
  }, [refinedItems]);

  let displayAttribute = attribute;
  if (attributeType === "attribute") {
    displayAttribute = attribute.replace("productAttributeDict.", "");
  }
  else if (attributeType === "variant") {
    displayAttribute = attribute.replace("variantSku.variantDisplay.", "");
    displayAttribute = attribute.replace(".meta.unitData.value", "");
  }

  return (
    <PrimAccordion.Item
      key={attribute}
      value={attribute}
    >
      <PrimAccordion.Trigger>
        <Flex
          flexGrow="1"
          justify="between"
        >
          {displayAttribute}
          {(refinedItem && refinedItem?.refinements.length > 0) && (
            <Badge>
              {refinedItem?.refinements.length}
            </Badge>
          )}
        </Flex>
      </PrimAccordion.Trigger>
      <PrimAccordion.Content>
        <div
          className="flex flex-col gap-2"
        >
          {items.map((item) => {
            return (
              <Text
                key={item.label}
                size="2"
                onClick={() => {
                  refine(item.value);
                }}
              >
                <Flex gap="2">
                  <Checkbox
                    checked={item.isRefined}
                  />
                  {item.label}
                  <Badge
                    size="1"
                    color={
                      item.isRefined
                        ? semanticColor.accent
                        : "gray"
                    }
                    variant="surface"
                  >
                    {item.count}
                  </Badge>
                </Flex>
              </Text>
            );
          })}
        </div>
      </PrimAccordion.Content>
    </PrimAccordion.Item>
  );
};

interface FacetToggleSwitchProps {
  attribute: string;
  attributeType: "attribute" | "variant";
}
const FacetToggleSwitch = (props: FacetToggleSwitchProps) => {
  const { attribute, attributeType } = props;

  let displayAttribute = attribute;
  if (attributeType === "attribute") {
    displayAttribute = attribute.replace("productAttributeDict.", "");
  }
  else if (attributeType === "variant") {
    displayAttribute = attribute.replace("variantSku.variantDisplay.", "");
    displayAttribute = displayAttribute.replace(".meta.booleanText.value", "");
  }

  return (
    <PrimAccordion.Item
      key={attribute}
      value={attribute}
    >
      <PrimAccordion.Trigger>
        <Flex
          flexGrow="1"
          justify="between"
        >
          {displayAttribute}
        </Flex>
      </PrimAccordion.Trigger>
      <PrimAccordion.Content>
        <ToggleSwitch
          attribute={attribute}
        />
      </PrimAccordion.Content>
    </PrimAccordion.Item>
  );
};

interface FacetRangeSliderProps {
  attribute: string;
  attributeType: "attribute" | "variant";
}
const FacetRangeSlider = (props: FacetRangeSliderProps) => {
  const { attribute, attributeType } = props;

  let displayAttribute = attribute;
  if (attributeType === "attribute") {
    displayAttribute = attribute.replace("productAttributeDict.", "");
  }
  else if (attributeType === "variant") {
    displayAttribute = attribute.replace("variantSku.variantDisplay.", "");
    displayAttribute = displayAttribute.replace(".meta.unitData.value", "");
  }

  return (
    <PrimAccordion.Item
      key={attribute}
      value={attribute}
    >
      <PrimAccordion.Trigger>
        {displayAttribute}
      </PrimAccordion.Trigger>
      <PrimAccordion.Content className="px-2">
        <RangeSlider
          attribute={attribute}
        />
      </PrimAccordion.Content>
    </PrimAccordion.Item>
  );
};

interface ProductFacetContentProps {
  className?: string;
}
const ProductFacetContent = (props: ProductFacetContentProps) => {
  const {
    categoryVariant,
    categoryAttribute,
  } = useProductListContext();

  const {
    className,
  } = props;

  const [collapsed, setCollapsed] = React.useState<string[]>([
    "categories",
    "price",
  ]);

  const includedAttributes = React.useMemo(() => {
    const attributeList: string[] = [
      "variantSku.promoPrice",
    ];

    categoryVariant.forEach((variant) => {
      if (variant.filterable) {
        if (variant.facetType === "rangeSlider") {
          attributeList.push(`variantSku.variantDisplay.${variant.label}.meta.unitData.value`);
        }
        else if (variant.facetType === "refinementList") {
          attributeList.push(`variantSku.variantDisplay.${variant.label}`);
        }
        else if (variant.facetType === "toggleRefinement") {
          attributeList.push(`variantSku.variantDisplay.${variant.label}.meta.booleanText.value`);
        }
      }
    });
    categoryAttribute.forEach((attribute) => {
      if (attribute.filterable) {
        if (attribute.facetType === "rangeSlider") {
          attributeList.push(`productAttributeDict.${attribute.label}`);
        }
        else if (attribute.facetType === "refinementList") {
          attributeList.push(`productAttributeDict.${attribute.label}`);
        }
      }
    });

    return attributeList;
  }, [categoryVariant, categoryAttribute]);

  const { refine: clearRefinements } = useClearRefinements({
    includedAttributes,
  });

  const onReset = () => {
    setCollapsed(["categories"]);
    clearRefinements();
  };

  return (
    <Flex
      className={cn(
        "size-full",
        className,
      )}
      direction="column"
    >
      <Flex
        align="baseline"
        direction="row"
        justify="between"
      >
        <Text
          semanticColor="accent"
          size="4"
          weight="medium"
        >
          Filters
        </Text>
        <Button
          onClick={onReset}
          semanticColor="warning"
          variant="ghost"
        >
          <RotateCcwIcon className="size-4" />
          Reset
        </Button>
      </Flex>
      <div className="relative mt-4 size-full">
        <div className="absolute inset-0">
          <ScrollArea
            className="h-full"
          >
            <PrimAccordion.Root
              onValueChange={setCollapsed}
              type="multiple"
              value={collapsed}
            >
              {/* Temp disabled, ticket PN-602 */}
              {/* <PrimAccordion.Item
                key="categories"
                value="categories"
              >
                <PrimAccordion.Trigger>
                  <Flex
                    flexGrow="1"
                    justify="between"
                  >
                    Categories
                  </Flex>
                </PrimAccordion.Trigger>
                <PrimAccordion.Content className="px-2">
                  <ProductHierarchical />
                </PrimAccordion.Content>
              </PrimAccordion.Item> */}
              <PrimAccordion.Item
                key="price"
                value="price"
              >
                <PrimAccordion.Trigger>
                  <Flex
                    flexGrow="1"
                    justify="between"
                  >
                    Price
                  </Flex>
                </PrimAccordion.Trigger>
                <PrimAccordion.Content className="px-2">
                  <RangeSlider
                    attribute="variantSku.promoPrice"
                    precision={0}
                  />
                </PrimAccordion.Content>
              </PrimAccordion.Item>
              {categoryVariant.map((variant) => {
                if (!variant.filterable) {
                  return null;
                }
                if (variant.facetType === "rangeSlider") {
                  return (
                    <FacetRangeSlider
                      key={variant.slug}
                      attribute={`variantSku.variantDisplay.${variant.label}.meta.unitData.value`}
                      attributeType="variant"
                    />
                  );
                }
                if (variant.facetType === "toggleRefinement") {
                  return (
                    <FacetToggleSwitch
                      key={variant.slug}
                      attribute={`variantSku.variantDisplay.${variant.label}.meta.booleanText.value`}
                      attributeType="variant"
                    />
                  );
                }
                if (variant.facetType === "refinementList") {
                  return (
                    <FacetRefinementList
                      key={variant.slug}
                      attribute={`variantSku.variantDisplay.${variant.slug}`}
                      attributeType="variant"
                    />
                  );
                }
                return null;
              })}
              {categoryAttribute.map((attribute) => {
                if (!attribute.filterable) {
                  return null;
                }
                if (attribute.facetType === "rangeSlider") {
                  return (
                    <FacetRangeSlider
                      key={attribute.slug}
                      attribute={`productAttributeDict.${attribute.label}`}
                      attributeType="attribute"
                    />
                  );
                }
                if (attribute.facetType === "toggleRefinement") {
                  return (
                    <FacetToggleSwitch
                      key={attribute.slug}
                      attribute={`productAttributeDict.${attribute.label}.meta.booleanText.value`}
                      attributeType="attribute"
                    />
                  );
                }
                if (attribute.facetType === "refinementList") {
                  return (
                    <FacetRefinementList
                      key={attribute.slug}
                      attribute={`productAttributeDict.${attribute.label}`}
                      attributeType="attribute"
                    />
                  );
                }
                return null;
              })}
            </PrimAccordion.Root>
          </ScrollArea>
        </div>
      </div>
    </Flex>
  );
};

export { ProductFacetContent };
